// OrgContext — v2 active-org state.
//
// CHG-1525 / ITER-3644 (OrgSwitcher real-data) replaces the
// CHG-1167 stub. Pre-CHG: stub returned { org: null, switchOrg: noop }
// and OrgSwitcher rendered 3 hardcoded MOCK_ORGS. Now:
//
//   - useOrg() exposes orgs (fetched), org (active selection), switchOrg,
//     isLoading, isError, plus a stable refetch fn.
//   - Fetch is a React Query query keyed by ["orgs", "list", account.id]
//     and gated to
//     authenticated admin accounts (account.has_staff_role ||
//     account.is_platform_admin) so donor-only sessions don't burn a 403.
//   - Active org id persists to localStorage under "active_org_id_v2" so
//     a refresh keeps the user on the same tenant. If the stored id is
//     not in the fetched set (revoked membership, tenant deleted), we
//     fall back to the first item and rewrite storage.
//   - switchOrg(id) updates state + storage. No backend mutation is needed;
//     the shared API client forwards the selection as X-Org-Context on the
//     next authenticated request.
//
// Provider stack note: OrgProvider sits BELOW AuthProvider in main.tsx
// so it can read useAuth() + skip the fetch for non-admin accounts.
// QueryClientProvider sits ABOVE AuthProvider so React Query is
// available here.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useQuery } from "@tanstack/react-query";
import { listOrganizations, type OrgListItem } from "../api/organizations";
import { useAuth } from "./AuthContext";
import {
  getActiveOrgId,
  removeActiveOrgId,
  setActiveOrgId as persistActiveOrgId,
} from "./active-org-storage";

// Backward-compatible exports for API helpers that predate the neutral
// storage module. New transport code imports active-org-storage directly.
export { ACTIVE_ORG_STORAGE_KEY, getActiveOrgId } from "./active-org-storage";

export interface Organization {
  id: string;
  name: string;
  slug: string;
  logo_url?: string | null;
}

export interface OrgContextValue {
  /** Fetched orgs visible to the authenticated account. Empty array
   *  while loading, on error, or for donor-only accounts. */
  orgs: Organization[];
  /** Currently active org (persisted to localStorage). null when no
   *  orgs are loaded yet or the account has no admin orgs. */
  org: Organization | null;
  /** Selected organization id, available before organization metadata loads.
   *  This is the transport/cache scope signal; `org` is the display model. */
  activeOrgId: string | null;
  /** True while the orgs query is in-flight (first fetch only — does
   *  not flip on background refetches). */
  isLoading: boolean;
  /** True when the orgs fetch failed; consumers (OrgSwitcher) surface
   *  an error state. */
  isError: boolean;
  /** Switch the active org. Persists to localStorage; no backend call.
   *  Caller is responsible for any downstream re-queries. */
  switchOrg: (orgId: string) => void;
  /** CHG-1562 / ITER-3678 — IA fix N3. Clear the active org back to
   *  null (and wipe localStorage). Surfaced via OrgSwitcher's
   *  "View as platform" item so a platform admin can return to the
   *  no-active-org state without devtools surgery. No-op for accounts
   *  with no active org. */
  clearActiveOrg: () => void;
  /** Force-refetch the orgs list (e.g. after creating a new org). */
  refetch: () => Promise<unknown>;
}

const noop = () => undefined;
const noopAsync = async () => undefined;

const OrgContext = createContext<OrgContextValue>({
  orgs: [],
  org: null,
  activeOrgId: null,
  isLoading: false,
  isError: false,
  switchOrg: noop,
  clearActiveOrg: noop,
  refetch: noopAsync,
});

export function OrgProvider({ children }: { children: ReactNode }) {
  const { account } = useAuth();

  // Gate the fetch: admin accounts (has_staff_role) OR platform-admins
  // hit /organizations; donor-only accounts skip (the endpoint 403s
  // for them, and the OrgSwitcher hides for donors anyway).
  const canFetch = Boolean(
    account &&
      (account.has_staff_role || account.is_platform_admin),
  );

  const query = useQuery({
    // Account identity is part of the root/bootstrap cache key. Without it,
    // a logout followed by another login inside staleTime can surface the
    // previous account's organization list without issuing a request.
    queryKey: ["orgs", "list", account?.id ?? "anonymous"] as const,
    queryFn: () => listOrganizations(),
    enabled: canFetch,
    staleTime: 60_000,
  });

  const orgs = useMemo<Organization[]>(
    () =>
      Array.isArray(query.data)
        ? query.data.map((o: OrgListItem) => ({
            id: o.id,
            name: o.name,
            slug: o.slug,
            logo_url: o.logo_url ?? null,
          }))
        : [],
    [query.data],
  );

  // Active-org selection. Initial value from storage; reconciled
  // against the fetched set on every orgs change.
  const [activeOrgId, setActiveOrgId] = useState<string | null>(() =>
    getActiveOrgId(),
  );

  // CHG-1551 / ITER-3668 — IA fix #1. The pre-fix effect auto-restored
  // activeOrgId to orgs[0] whenever the stored id was null or stale.
  // For a platform admin with N orgs this meant `!activeOrg` was
  // unreachable from the UI — the Sidebar org-context layering
  // (CHG-1540 step 5) could never enter its "ONLY Platform group"
  // branch because activeOrg always re-populated milliseconds after a
  // storage wipe. Field-test report B1 captured the breakage.
  //
  // Post-fix: only RECONCILE a stale stored id (one that doesn't match
  // any org in the fetched set) by clearing it. NEVER auto-pick orgs[0].
  // A platform admin lands at /platform until they explicitly pick a
  // tenant via OrgSwitcher; org-admin accounts whose single org is in
  // the fetched set retain whatever was stored (no change to that path).
  // Org-admin accounts with no stored selection now also stay null —
  // OrgSwitcher's "no active org" state is visible until they click.
  useEffect(() => {
    if (orgs.length === 0) return;
    if (activeOrgId && !orgs.some((o) => o.id === activeOrgId)) {
      // Stored id no longer valid (org deleted, membership revoked,
      // role narrowed). Clear so the UI surfaces the no-active-org
      // state instead of silently jumping to a different tenant.
      setActiveOrgId(null);
      removeActiveOrgId();
    }
    // No auto-pick. Whatever activeOrgId is (null included), keep it.
  }, [orgs, activeOrgId]);

  // CHG-1562 / ITER-3678 — IA fix N1. Clear storage ONLY on a real
  // logout TRANSITION (was authenticated, now null), NOT on the initial-
  // null window during AuthProvider's /users/me hydration.
  //
  // Pre-fix: this effect fired any time account === null, including the
  // first render between mount and /me resolution. That wiped
  // localStorage.active_org_id_v2 on every page reload BEFORE the user's
  // session was even confirmed, so the CHG-1551 "no-auto-restore"
  // behavior + the Sidebar org-context layering combined to surface
  // the user as "no active org" after every reload. Field-test report
  // v3 N1 captured this as the headline regression.
  //
  // Post-fix: use a ref to track the previous account value. Only fire
  // when the previous render had a non-null account AND the current
  // render is null — that's the genuine logout transition. The initial
  // render writes the ref but does not fire the clear (prevAccount is
  // still `undefined` sentinel).
  const prevAccountRef = useRef<typeof account | undefined>(undefined);
  useEffect(() => {
    const prev = prevAccountRef.current;
    prevAccountRef.current = account;
    // Skip the very first render (prev === undefined). Only the
    // transition `prev !== null && account === null` is a real logout.
    if (prev !== undefined && prev !== null && account === null) {
      setActiveOrgId(null);
      removeActiveOrgId();
    }
  }, [account]);

  const switchOrg = useCallback(
    (orgId: string) => {
      if (!orgs.some((o) => o.id === orgId)) return; // ignore unknown
      setActiveOrgId(orgId);
      persistActiveOrgId(orgId);
    },
    [orgs],
  );

  // CHG-1562 / ITER-3678 — IA fix N3. Explicit user-initiated clear,
  // surfaced via OrgSwitcher's "View as platform" item. Same state +
  // storage transitions as the logout-clear path, but driven by a UI
  // event so it bypasses the prevAccountRef gate above.
  const clearActiveOrg = useCallback(() => {
    setActiveOrgId(null);
    removeActiveOrgId();
  }, []);

  const refetch = useCallback(async () => {
    if (!canFetch) return undefined;
    return query.refetch();
  }, [canFetch, query]);

  const org = useMemo<Organization | null>(
    () => orgs.find((o) => o.id === activeOrgId) ?? null,
    [orgs, activeOrgId],
  );

  const value = useMemo<OrgContextValue>(
    () => ({
      orgs,
      org,
      activeOrgId,
      isLoading: query.isLoading,
      isError: query.isError,
      switchOrg,
      clearActiveOrg,
      refetch,
    }),
    [orgs, org, activeOrgId, query.isLoading, query.isError, switchOrg, clearActiveOrg, refetch],
  );

  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>;
}

export function useOrg(): OrgContextValue {
  return useContext(OrgContext);
}
